import { DateService } from '@/coreDomainsModule/services/dateService';
import { ForecastTableContentViewModel } from '../../baseTableContentViewModel';
import { ForecastForReceiptHeaderKey, isCustomerAnnounceForReceiptKey } from '../../interface/tableContentHeaders';
import { TableContentItem } from '../../interface/tableContentItems';
import { convertIDateToDateFormat, convertUnixTimeToIDate } from '../../utils/dateFormat';
import { TableContentViewModel } from '@/viewModels/tableContent';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DiffAmountType, ForecastDiffColumnTypes } from './domains/entities/forecastDiffLastMonth';
import { i18n } from '@/i18n';
import { ForecastForReceiptTableContentViewModel } from './forecastForReceiptTableContentViewModel';

/**
 * 内示前月差異のテーブル表示に使うロジック
 */
export class ForecastDiffLastMonthTableContentViewModel extends ForecastForReceiptTableContentViewModel {
  t = i18n.t;

  /**
   * 内示前月差異の項目表示用
   * @param headerKey - ヘッダーキー
   * @param columnType - 列タイプ
   * @param item - テーブルコンテンツアイテム
   * @param forecastStartYearMonth - 内示の開始年月
   */
  public getDisplayForecastDiffItem = (
    headerKey: ForecastForReceiptHeaderKey,
    columnType: ForecastDiffColumnTypes,
    item: TableContentItem,
    forecastStartYearMonth?: IYearMonth
  ): string => {
    if (forecastStartYearMonth === undefined || forecastStartYearMonth === null) return '';

    const forecastIndex = this._getForecastIndex(headerKey);
    switch (columnType) {
      case 'lastMonthAmount':
        return this._getDisplayForecastDiffItem(forecastIndex, item, forecastStartYearMonth, columnType);
      case 'currentMonthAmount':
        return this._getDisplayForecastDiffItem(forecastIndex, item, forecastStartYearMonth, columnType);
      case 'diffAmount':
        return this._getDisplayForecastDiffAmountValue(forecastIndex, item, forecastStartYearMonth);
      default:
        return '';
    }
  };

  /**
   * 内示前月差異のヘッダーのテキストを取得する
   * @param columnType - 列タイプ
   */
  public getForecastDiffHeaderText = (columnType: ForecastDiffColumnTypes): string => {
    const t = this.t;
    switch (columnType) {
      case 'lastMonthAmount':
        return t('前月');
      case 'currentMonthAmount':
        return t('今月');
      case 'diffAmount':
        return t('前月比');
      default:
        return '';
    }
  };

  /**
   * 内示前月差異の差異数量のステータスを取得する
   * @param headerKey - ヘッダーキー
   * @param item - テーブルコンテンツアイテム
   * @param forecastStartYearMonth - 内示の開始年月
   */
  public getDiffAmountStatus = (
    headerKey: ForecastForReceiptHeaderKey,
    item: TableContentItem,
    forecastStartYearMonth?: IYearMonth
  ): DiffAmountType => {
    if (forecastStartYearMonth === undefined || forecastStartYearMonth === null) return 'equal';

    const forecastIndex = this._getForecastIndex(headerKey);
    const customerAnnounce = this._getTargetCustomerForecast(forecastIndex, item, forecastStartYearMonth);
    const diffAmount = customerAnnounce?.diffAmount;
    if (diffAmount === undefined || diffAmount === null || diffAmount === 0) return 'equal';

    return diffAmount > 0 ? 'up' : 'down';
  };

  /**
   * 客先内示を取得する
   * @param index - 内示のインデックス
   * @param item - テーブルの表示内容
   * @param forecastStartYearMonth - 内示の開始年月
   */
  _getTargetCustomerForecast = (index: number, item: TableContentItem, forecastStartYearMonth: IYearMonth) => {
    const customerAnnounces = item.customerForecasts;
    if (!customerAnnounces) return '';

    const date = this.getIYearMonth(
      forecastStartYearMonth ? new Date(forecastStartYearMonth.year, forecastStartYearMonth.month - 1) : new Date(),
      index
    );
    const yearMonthInfo = { year: date.year, month: date.month };
    const announceIndex = customerAnnounces.findIndex(
      (item) => item.targetYearMonth.year === yearMonthInfo.year && item.targetYearMonth.month === yearMonthInfo.month
    );

    return announceIndex !== undefined && announceIndex !== -1 ? customerAnnounces[announceIndex] : '';
  };

  /**
   * 内示列の表示内容
   * @param index - 内示のインデックス
   * @param item - テーブルの表示内容
   * @param forecastStartYearMonth - 内示の開始年月
   * @param columnType - 列タイプ
   */
  _getDisplayForecastDiffItem = (
    index: number,
    item: TableContentItem,
    forecastStartYearMonth: IYearMonth,
    columnType: ForecastDiffColumnTypes
  ): string => {
    const customerAnnounce = this._getTargetCustomerForecast(index, item, forecastStartYearMonth);

    switch (columnType) {
      case 'lastMonthAmount':
        return customerAnnounce?.lastMonthAmount?.addComma() ?? '-';
      case 'currentMonthAmount':
        return customerAnnounce?.currentMonthAmount?.addComma() ?? '-';
      default:
        return '';
    }
  };

  /**
   * 内示前月差異の差異数量の表示内容を取得する
   * @param index - 内示のインデックス
   * @param item - テーブルの表示内容
   * @param forecastStartYearMonth - 内示の開始年月
   */
  _getDisplayForecastDiffAmountValue = (index: number, item: TableContentItem, forecastStartYearMonth: IYearMonth): string => {
    const customerAnnounce = this._getTargetCustomerForecast(index, item, forecastStartYearMonth);
    const diffAmount = customerAnnounce?.diffAmount;
    if (diffAmount === undefined || diffAmount === null) return '-';

    if (diffAmount === 0) {
      return '0';
    }

    const diffAmountStr = diffAmount.addComma();
    if (diffAmount > 0) {
      // 正の数の場合は+をつける
      return `+${diffAmountStr}`;
    }
    return diffAmountStr;
  };
}
